/* eslint-disable @typescript-eslint/no-unsafe-return */
import { ITokenBasedSessionService } from '@studyportals/student-interfaces';
import { IStudent, StudentField } from '@studyportals/studentdomain';
import config from 'config';
import { LastStateChangeHash, StudentClient } from '../interfaces/student-client';
import { DataStorage } from "@studyportals/data-storage";
import { StudentAPIUnauthorizedError } from "../errors/student-api-unauthorized-error";
import { StudentAPIValidationError } from "../errors/student-api-validation-error";
import { StudentAPIStudentNotFoundError } from "../errors/student-api-student-not-found-error";
import { StudentAPIBaseError } from "../errors/student-api-base-error";
import { StudentAPIUnknownError } from "../errors/student-api-unknown-error";
import { StudentAPISessionNotFoundError } from "../errors/student-api-session-not-found";

interface IUnauthorizedError extends StudentAPIUnauthorizedError {
	data: {
		errorBody: string;
		startTime: number;
		requestDuration: number;
		computerTime: number;
		expireTime: number;
		serverTime: any;
	};
}

export class StudentAPIClient implements StudentClient {
	constructor(private sessionService: ITokenBasedSessionService) {}

	private requestMap: Map<StudentField, Promise<IStudent>> = new Map<StudentField, Promise<IStudent>>();

	private get fetch(): any {
		return fetch;
	}

	public async getData(studentFields: StudentField[]): Promise<IStudent> {
		try {
			return await this.getDataWithRequestDeduplication(studentFields, false);
		} catch (error) {
			if (error instanceof StudentAPIUnauthorizedError) {
				return this.getDataWithRequestDeduplication(studentFields, true);
			}

			throw error;
		}
	}

	private async patchData(body: object): Promise<LastStateChangeHash> {
		const start = Date.now();
		const accessToken = await this.getAccessToken(false);

		const request = await fetch(`${config.STUDENTAPI_BASE_URL}/data`, {
			method: 'PATCH',
			headers: {
				Authorization: accessToken
			},
			body: JSON.stringify(body)
		});

		if (!request.ok) {
			let errorBody: string;
			try {
				errorBody = await request.text();
			} catch {
				errorBody = 'Could not read error details';
			}

			if (request.status === 401) {
				throw this.getUnauthorizedError(
					errorBody,
					start,
					accessToken
				);
			}

			throw new Error(`Failed to patch student data: ${errorBody}`);
		}

		const responseBody = await request.json();
		return responseBody[StudentField.LAST_STATE_CHANGE_HASH];
	}

	public setData(studentData: IStudent): Promise<LastStateChangeHash> {
		return this.patchData(studentData);
	}

	public async addToCollection(type: StudentField, items: any[]): Promise<string> {
		return this.patchData({
			[`${type}_add`]: items
		});
	}

	public async removeFromCollection(type: StudentField, items: any[]): Promise<string> {
		return this.patchData({
			[`${type}_remove`]: items
		});
	}

	public async replaceCollection(type: StudentField, items: any[]): Promise<string> {
		return this.patchData({
			[`${type}_replace`]: items
		});
	}

	private async getDataWithRequestDeduplication(studentFields: StudentField[], forceRefresh: boolean): Promise<IStudent> {
		const actions = this.getActiveDataRequestsAndFieldsToRequest(studentFields);

		const activeDataRequests = actions.activeDataRequests;
		const studentFieldsToRequest = actions.studentFieldsToRequest;

		if (studentFieldsToRequest.length > 0) {
			activeDataRequests.push(this.retrieveStudentFields(studentFieldsToRequest, forceRefresh));
		}

		return this.mergeStudentDataResponses(await Promise.all(activeDataRequests));
	}

	private getActiveDataRequestsAndFieldsToRequest(studentFields: StudentField[]): {
		activeDataRequests: Promise<IStudent>[];
		studentFieldsToRequest: StudentField[];
	} {
		const activeDataRequests: Promise<IStudent>[] = [];
		const studentFieldsToRequest: StudentField[] = [];

		studentFields.forEach((field) => {
			const request = this.requestMap.get(field);

			if (request) {
				activeDataRequests.push(request);
				return;
			}

			studentFieldsToRequest.push(field);
		});

		return { activeDataRequests, studentFieldsToRequest };
	}

	private async retrieveStudentFields(studentFields: StudentField[], forceRefresh: boolean): Promise<IStudent> {
		const request = this.requestStudentData(studentFields, forceRefresh);

		studentFields.forEach((field) => {
			this.requestMap.set(field, request);
		});

		try {
			return await request;
		} catch (error) {
			if (error instanceof  StudentAPIBaseError) throw error;

			throw new StudentAPIUnknownError(error as Error);
		} finally {
			studentFields.forEach((field) => {
				this.requestMap.delete(field);
			});
		}
	}

	private mergeStudentDataResponses(responses: IStudent[]): IStudent {
		let data: IStudent = {};
		responses.forEach((response) => {
			data = { ...data, ...response };
		});

		return data;
	}

	private async requestStudentData(studentFields: StudentField[],  forceRefresh: boolean): Promise<IStudent> {
		const start = Date.now();
		const accessToken = await this.getAccessToken(forceRefresh);

		const request = await fetch(`${config.STUDENTAPI_BASE_URL}/data?scope=${studentFields.join(',')}`, {
			headers: {
				Authorization: accessToken
			}
		});

		if (!request.ok) {
			let errorBody: string;
			try {
				errorBody = await request.text();
			} catch {
				errorBody = 'Could not read error details';
			}

			if (request.status === 401) {
				throw this.getUnauthorizedError(
					errorBody,
					start,
					accessToken
				);
			}

			if (request.status === 400) {
				throw new StudentAPIValidationError();
			}

			if (request.status === 404) {
				throw new StudentAPIStudentNotFoundError();
			}

			throw new Error(`Failed to retrieve data from StudentAPI: ${errorBody}`);
		}

		const data = await request.json() as Record<string, any>;
		Object.keys(data).forEach((key) => {
			if (data[key] === null) {
				data[key] = undefined;
			}
		});

		return data as IStudent;
	}

	private async getAccessToken(forceRefresh: boolean): Promise<string> {
		const session = await this.sessionService.getSession();

		if (!session) {
			throw new StudentAPISessionNotFoundError();
		}
		if (forceRefresh) await session.refreshCredentials();

		return session.getAccessToken();
	}

	private getUnauthorizedError(
		errorBody: string,
		start: number,
		accessToken: string
	): IUnauthorizedError {
		const error = new StudentAPIUnauthorizedError() as IUnauthorizedError;
		error.data = {
			errorBody,
			startTime: start,
			requestDuration: Date.now() - start,
			computerTime: Date.now(),
			expireTime: 0,
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			serverTime: DataStorage.retrieve('SessionService/CachedTime')
		};
		try {
			const tokenPayload = JSON.parse(atob(accessToken.split('.')[1])) as { exp: number };
			error.data.expireTime = tokenPayload.exp;
		} catch {
			// expireTime is already set to 0 as default
		}
		return error;
	}
}
